import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { ContactSupportMessageType } from "@redotech/redo-model/conversation/message-parsers/contact-support-message-parser";
import { memo } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { Flex } from "../../flex";
import { HandleEmailInputFn, HandleLiveChatHandoffFn } from "../common";
import { AiEmailInput } from "./ai-email-input";
import { ChatContentRenderer } from "./chat-content-renderer";
import {
  GenericConfirmedState,
  GenericRejectedState,
} from "./chat-content-states";
import { getConfirmedStateContent } from "./tool-message-utils";

const ExternalEmailButton = memo(function ExternalEmailButton({
  message,
  readOnly,
  onEmailEntry,
  conversationId,
  messageSide = "left",
}: {
  message: ContactSupportMessageType;
  readOnly: boolean;
  onEmailEntry?: HandleEmailInputFn;
  conversationId: string;
  messageSide?: "left" | "right";
}) {
  const primaryEmail = message.structuredContent?.externalEmail || "";

  const [clickLoad, doClickLoad] = useTriggerLoad(async () => {
    if (onEmailEntry) {
      // Call handleContactSupport with isExternalEmail flag
      await onEmailEntry({
        conversationId,
        messageId: message._id,
        isExternalEmail: true,
      });
    }
    // Open mailto link
    if (primaryEmail) {
      window.location.href = `mailto:${primaryEmail}`;
    }
  });

  const handleClick = useHandler(() => {
    doClickLoad();
  });

  const disabled = readOnly || clickLoad.pending || !primaryEmail;

  return (
    <Flex alignSelf="stretch" dir="column" gap="md" px="lg" py="md" w="full">
      <RedoButton
        disabled={disabled}
        hierarchy="primary"
        onClick={handleClick}
        style={{ width: "100%" }}
        text={`Email support at ${primaryEmail}`}
      />
    </Flex>
  );
});
export type ContactSupportMode = "email" | "live_chat" | "external_email";
export const ContactSupport = memo(function ContactSupport({
  message,
  readOnly,
  onEmailEntry,
  onLiveChatHandoff,
  conversationId,
  buttonText,
  messageSide = "left",
  mode = "email",
  isExternalEmail = false,
}: {
  message: ContactSupportMessageType;
  readOnly: boolean;
  onEmailEntry?: HandleEmailInputFn | undefined;
  onLiveChatHandoff?: HandleLiveChatHandoffFn | undefined;
  conversationId: string;
  buttonText: string;
  messageSide?: "left" | "right";
  mode?: ContactSupportMode;
  isExternalEmail?: boolean;
}) {
  const isLiveChatMode = mode === "live_chat";
  const isExternalEmailMode = !!message.structuredContent?.externalEmail;

  const { title: confirmedTitle, subtitle: confirmedSubtitle } =
    getConfirmedStateContent(
      message,
      isLiveChatMode
        ? "You'll be connected to a live agent shortly!"
        : isExternalEmailMode
          ? "You are being redirected to contact support via email."
          : `We'll get back to you ${message.structuredContent?.customerEmail ? `at ${message.structuredContent?.customerEmail}` : "over email."}`,
      "Something went wrong contacting support.",
      isLiveChatMode
        ? "Connecting to live support..."
        : "Thank you for contacting support.",
    );

  return (
    <ChatContentRenderer
      contentByState={{
        pending: isExternalEmailMode ? (
          <ExternalEmailButton
            conversationId={conversationId}
            message={message}
            messageSide={messageSide}
            onEmailEntry={onEmailEntry}
            readOnly={readOnly}
          />
        ) : (
          <AiEmailInput
            buttonText={buttonText}
            conversationId={conversationId}
            message={message}
            messageSide={messageSide}
            mode={mode}
            onEmailEntry={isLiveChatMode ? onLiveChatHandoff : onEmailEntry}
            readOnly={readOnly}
          />
        ),
        confirmed: (
          <GenericConfirmedState
            subtitle={confirmedSubtitle}
            title={confirmedTitle}
          />
        ),
        rejected: (
          <GenericRejectedState
            subtitle="Please try again or contact us directly"
            title="Unable to submit support request"
          />
        ),
      }}
      message={message}
    />
  );
});
